/**
 * User roles — used for RBAC across all endpoints + UI guards.
 * Order matters: higher index = more privilege.
 */
export const USER_ROLES = ['Staff', 'Senior', 'Manager', 'Partner', 'Admin'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** Numeric privilege level — use for `level >= X` checks instead of string compares. */
export const ROLE_LEVEL: Record<UserRole, number> = {
  Staff: 1,
  Senior: 2,
  Manager: 3,
  Partner: 4,
  Admin: 5,
};

/**
 * Timesheet entry approval state machine.
 *
 *   draft → submitted → manager_approved → partner_approved
 *                ↓ (rejected by either approver)
 *            rejected ← (can be reset to draft by owner)
 *                ↓
 *              draft
 *
 * `partner_approved` is terminal — locked from further edits.
 */
export const TIMESHEET_STATES = [
  'draft',
  'submitted',
  'manager_approved',
  'partner_approved',
  'rejected',
] as const;
export type TimesheetState = (typeof TIMESHEET_STATES)[number];

/**
 * Allowed state transitions — validated server-side before mutating.
 * Key = current state, value = states reachable from it.
 */
export const TIMESHEET_TRANSITIONS: Record<TimesheetState, readonly TimesheetState[]> = {
  draft: ['submitted'],
  submitted: ['manager_approved', 'rejected', 'draft'],
  manager_approved: ['partner_approved', 'rejected'],
  partner_approved: [], // terminal — no further transitions
  rejected: ['draft'],
};

/**
 * Roles permitted to perform each transition.
 * Key format: `${from}→${to}`.
 */
export const TRANSITION_ROLES: Record<string, readonly UserRole[]> = {
  'draft→submitted': ['Staff', 'Senior', 'Manager', 'Partner', 'Admin'],
  'submitted→manager_approved': ['Manager', 'Partner', 'Admin'],
  'submitted→rejected': ['Manager', 'Partner', 'Admin'],
  'submitted→draft': ['Staff', 'Senior', 'Manager', 'Partner', 'Admin'], // owner can recall
  'manager_approved→partner_approved': ['Partner', 'Admin'],
  'manager_approved→rejected': ['Partner', 'Admin'],
  'rejected→draft': ['Staff', 'Senior', 'Manager', 'Partner', 'Admin'],
};

/**
 * Pure helper — checks if a transition is structurally allowed.
 * Does NOT check role permissions (use TRANSITION_ROLES for that).
 */
export function isValidTransition(from: TimesheetState, to: TimesheetState): boolean {
  return TIMESHEET_TRANSITIONS[from].includes(to);
}

/**
 * Clock event types — paired (clock_in then clock_out, server enforces).
 */
export const CLOCK_EVENT_TYPES = ['clock_in', 'clock_out'] as const;
export type ClockEventType = (typeof CLOCK_EVENT_TYPES)[number];
